import { useState, useEffect } from 'react';
import type { MetricGoal, MetricDirection } from '@monitoring-panel/types';
import { PREDEFINED_METRIC_DEFAULTS } from '@monitoring-panel/types';

interface MetricGoalFormProps {
  metricId: string;
  goal: MetricGoal | undefined;
  onChange: (goal: MetricGoal | undefined) => void;
}

const EMPTY_GOAL: MetricGoal = {
  target: 0,
  direction: 'higher_is_better',
  greenBoundary: 0,
  amberBoundary: 0,
};

function parseDecimal(value: string): number {
  return parseFloat(value.replace(',', '.')) || 0;
}

function useDecimalInput(externalValue: number, onCommit: (v: number) => void) {
  const [text, setText] = useState(String(externalValue));

  useEffect(() => {
    setText(String(externalValue));
  }, [externalValue]);

  const handleChange = (raw: string) => {
    setText(raw.replace(/[^0-9.,-]/g, ''));
  };

  const handleBlur = () => {
    const parsed = parseDecimal(text);
    onCommit(parsed);
    setText(String(parsed));
  };

  return { text, handleChange, handleBlur };
}

const DIRECTION_LABELS: Record<MetricDirection, string> = {
  higher_is_better: '↑ Higher is better',
  lower_is_better: '↓ Lower is better',
  close_to_target: '⊕ Close to target',
};

function getBoundaryLabels(direction: MetricDirection) {
  switch (direction) {
    case 'higher_is_better':
      return {
        green: 'Green if ≥',
        greenHelp: 'Value at or above this = green',
        amber: 'Amber if ≥',
        amberHelp: 'Value at or above this = amber (below = red)',
      };
    case 'lower_is_better':
      return {
        green: 'Green if ≤',
        greenHelp: 'Value at or below this = green',
        amber: 'Amber if ≤',
        amberHelp: 'Value at or below this = amber (above = red)',
      };
    case 'close_to_target':
      return {
        green: 'Green range (±)',
        greenHelp: 'Max distance from target for green',
        amber: 'Amber range (±)',
        amberHelp: 'Max distance from target for amber',
      };
  }
}

export function MetricGoalForm({ metricId, goal, onChange }: MetricGoalFormProps) {
  const current = goal || PREDEFINED_METRIC_DEFAULTS[metricId] || EMPTY_GOAL;

  const commitField = (field: keyof MetricGoal) => (value: number) => {
    onChange({ ...current, [field]: value });
  };

  const target = useDecimalInput(current.target, commitField('target'));
  const green = useDecimalInput(current.greenBoundary, commitField('greenBoundary'));
  const amber = useDecimalInput(current.amberBoundary, commitField('amberBoundary'));

  const labels = getBoundaryLabels(current.direction);

  const handleDirectionChange = (dir: MetricDirection) => {
    onChange({ ...current, direction: dir });
  };

  const inputClass = 'adv-input adv-input--sm w-full';
  const numberInputClass = `${inputClass} adv-num`;

  return (
    <div className="space-y-3 p-3 bg-adv-sunken rounded-adv-lg">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="adv-field-label">Target</label>
          <input
            type="text"
            inputMode="decimal"
            value={target.text}
            onChange={(e) => target.handleChange(e.target.value)}
            onBlur={target.handleBlur}
            className={numberInputClass}
          />
        </div>

        <div>
          <label className="adv-field-label">Direction</label>
          <select
            value={current.direction}
            onChange={(e) => handleDirectionChange(e.target.value as MetricDirection)}
            className={inputClass}
          >
            <option value="higher_is_better">{DIRECTION_LABELS.higher_is_better}</option>
            <option value="lower_is_better">{DIRECTION_LABELS.lower_is_better}</option>
            <option value="close_to_target">{DIRECTION_LABELS.close_to_target}</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="adv-field-label">
            <span className="inline-block w-2 h-2 rounded-full bg-adv-success mr-1" />
            {labels.green}
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={green.text}
            onChange={(e) => green.handleChange(e.target.value)}
            onBlur={green.handleBlur}
            className={numberInputClass}
          />
          <p className="text-adv-body-s text-adv-tertiary mt-1">{labels.greenHelp}</p>
        </div>

        <div>
          <label className="adv-field-label">
            <span className="inline-block w-2 h-2 rounded-full bg-adv-gold mr-1" />
            {labels.amber}
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={amber.text}
            onChange={(e) => amber.handleChange(e.target.value)}
            onBlur={amber.handleBlur}
            className={numberInputClass}
          />
          <p className="text-adv-body-s text-adv-tertiary mt-1">{labels.amberHelp}</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => onChange(undefined)}
          className="adv-btn adv-btn--danger adv-btn--sm"
        >
          Remove goal
        </button>
      </div>
    </div>
  );
}
